
import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Introduction to Saving & Investing',
    content: [
      '<strong>Learning Goal:</strong> Learn how to save and invest wisely, using programs and tools available to Filipinos.',
      'Saving and investing are key to building wealth and financial security. Start small and grow your savings over time.'
    ]
  },
  {
    title: 'Government Savings Programs',
    content: [
      'Explore <a href="https://www.pagibigfund.gov.ph/" target="_blank">Pag-IBIG Fund</a> and <a href="https://www.sss.gov.ph/" target="_blank">SSS PESO Fund</a> for safe, government-backed savings options.'
    ]
  },
  {
    title: 'Low-Risk Investments',
    content: [
      'Consider government bonds, time deposits, and UITFs from trusted banks. Learn about risks and returns before investing.'
    ]
  },
  {
    title: 'Tips for Filipino Investors',
    content: [
      'Attend free financial literacy webinars from <a href="https://www.bsp.gov.ph/Pages/ConsumerFinance/FinancialEducation.aspx" target="_blank">BSP</a> and <a href="https://www.pfa.org.ph/" target="_blank">PFA</a>. Avoid scams and always check if an investment is registered with the SEC.'
    ]
  }
];

const FinancialPlanningModule2Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep } = useModuleProgress('financialPlanningM2');
  const totalSteps = moduleContent.length;
  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;
  const isLastStep = safeCurrentStep === totalSteps - 1;

  const handleNext = () => {
    if (safeCurrentStep < totalSteps - 1) {
      setCurrentStep(safeCurrentStep + 1);
    }
  };
  const handleBack = () => {
    if (safeCurrentStep === 0) {
      navigate('/panel/financial-planning');
      return;
    }
    setCurrentStep(safeCurrentStep - 1);
  };

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left financial-panel">
          <div className="module-header">
            <h1>Module 2</h1>
            <p className="module-subtitle">Saving & Investing</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Discover practical ways to save money and learn the basics of investing for Filipinos, including emergency funds and investment options.
            </p>
          </div>
        </div>
        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {safeCurrentStep + 1} of {totalSteps}
              </span>
            </div>
            <div className="module-section">
              <h2 className="module-title">{currentModule.title}</h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>
            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph, index) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph, index) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {safeCurrentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>
            {isLastStep ? (
              <button className="btn-primary" onClick={() => navigate('/panel/financial-planning')}>Finish Module</button>
            ) : (
              <button className="btn-primary" onClick={handleNext}>Next</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialPlanningModule2Page;

